import React, { useState, useEffect, useRef, useCallback } from "react";
import Popover from "@mui/material/Popover";
import MuiTextField from "@mui/material/TextField";
import { AccessTimeOutlined } from "@material-ui/icons";
import Button from "../button/button";
import locale from "localization";
import { isDate } from "lodash";
import { combineDateAndTimeInput } from "utils";
import moment from "moment";
import classNames from "classnames";
import styles from "./time-picker-input.module.scss";

const FORMAT = "h:mm A";

const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

const TimePickerInput = ({
  value,
  onChange,
  name,
  date = new Date(),
  placeholder,
  actions,
  className,
  disabled,
  error,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [displayText, setDisplayText] = useState("");

  // Popover edit state
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("00");
  const [period, setPeriod] = useState("AM");

  const hourRef = useRef();
  const minuteRef = useRef();

  useEffect(() => {
    if (isDate(value)) {
      setDisplayText(moment(value).format(FORMAT));
    } else {
      setDisplayText("");
    }
  }, [value]);

  const initFromValue = useCallback(() => {
    if (isDate(value)) {
      const h = value.getHours();
      const m = value.getMinutes();
      const isPM = h >= 12;
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      setHour(String(h12));
      setMinute(String(m).padStart(2, "0"));
      setPeriod(isPM ? "PM" : "AM");
    } else {
      setHour("");
      setMinute("00");
      setPeriod("AM");
    }
  }, [value]);

  const openPicker = useCallback(
    (e) => {
      if (disabled) return;
      initFromValue();
      setAnchorEl(e.currentTarget);
      setOpen(true);
      setTimeout(() => {
        hourRef.current?.focus();
        hourRef.current?.select();
      }, 50);
    },
    [disabled, initFromValue]
  );

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleOk = useCallback(() => {
    const h = clamp(parseInt(hour) || 12, 1, 12);
    const m = clamp(parseInt(minute) || 0, 0, 59);

    let hours24 = h;
    if (period === "PM" && h !== 12) hours24 = h + 12;
    if (period === "AM" && h === 12) hours24 = 0;

    const timeBase = new Date();
    timeBase.setHours(hours24, m, 0, 0);
    const result = combineDateAndTimeInput(date || new Date(), timeBase);

    onChange?.(name, { value: result });
    setDisplayText(moment(result).format(FORMAT));
    handleClose();
  }, [hour, minute, period, date, name, onChange]);

  // ── Hour input ───────────────────────────────────────────────────────────────
  const handleHourChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (!raw) { setHour(""); return; }

    const num = parseInt(raw);

    if (raw.length === 1) {
      if (num >= 1 && num <= 9) {
        setHour(raw);
        // digits 2-9 can only be single-digit in 1-12, auto-advance
        if (num >= 2) {
          minuteRef.current?.focus();
          minuteRef.current?.select();
        }
      }
      return;
    }

    // Two digits: valid range 10-12
    if (num >= 10 && num <= 12) {
      setHour(raw);
      minuteRef.current?.focus();
      minuteRef.current?.select();
    }
  };

  const handleHourKeyDown = (e) => {
    if (e.key === "ArrowUp") {
      const h = clamp((parseInt(hour) || 0) + 1, 1, 12);
      setHour(String(h));
    } else if (e.key === "ArrowDown") {
      const h = clamp((parseInt(hour) || 13) - 1, 1, 12);
      setHour(String(h));
    } else if (e.key === "Tab" || e.key === "Enter") {
      minuteRef.current?.focus();
      minuteRef.current?.select();
    }
  };

  const handleHourBlur = () => {
    if (hour === "0" || hour === "") return;
    const num = parseInt(hour);
    if (num < 1 || num > 12) setHour("12");
  };

  // ── Minute input ─────────────────────────────────────────────────────────────
  const handleMinuteChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (!raw) { setMinute(""); return; }
    const num = parseInt(raw);
    if (num >= 0 && num <= 59) setMinute(raw);
  };

  const handleMinuteKeyDown = (e) => {
    if (e.key === "ArrowUp") {
      const m = clamp((parseInt(minute) || 0) + 1, 0, 59);
      setMinute(String(m).padStart(2, "0"));
    } else if (e.key === "ArrowDown") {
      const m = clamp((parseInt(minute) || 1) - 1, 0, 59);
      setMinute(String(m).padStart(2, "0"));
    }
  };

  const handleMinuteBlur = () => {
    const m = parseInt(minute);
    if (!isNaN(m) && m >= 0 && m <= 59) {
      setMinute(String(m).padStart(2, "0"));
    } else {
      setMinute("00");
    }
  };

  return (
    <>
      <MuiTextField
        value={displayText}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        className={classNames(styles.input, className)}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <AccessTimeOutlined className={styles.clockTrigger} onClick={openPicker} />
          ),
        }}
        onClick={openPicker}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: styles.popoverPaper }}
      >
        <div className={styles.picker}>
          <p className={styles.title}>Enter Time</p>

          <div className={styles.timeRow}>
            {/* Hour */}
            <div className={styles.inputGroup}>
              <input
                ref={hourRef}
                className={styles.timeBox}
                value={hour}
                onChange={handleHourChange}
                onKeyDown={handleHourKeyDown}
                onBlur={handleHourBlur}
                onFocus={(e) => e.target.select()}
                maxLength={2}
                inputMode="numeric"
                placeholder="--"
              />
              <span className={styles.boxLabel}>Hour</span>
            </div>

            <span className={styles.colon}>:</span>

            {/* Minute */}
            <div className={styles.inputGroup}>
              <input
                ref={minuteRef}
                className={classNames(styles.timeBox, styles.minuteBox)}
                value={minute}
                onChange={handleMinuteChange}
                onKeyDown={handleMinuteKeyDown}
                onBlur={handleMinuteBlur}
                onFocus={(e) => e.target.select()}
                maxLength={2}
                inputMode="numeric"
                placeholder="00"
              />
              <span className={styles.boxLabel}>Minute</span>
            </div>

            {/* AM/PM */}
            <div className={styles.periodGroup}>
              <button
                className={classNames(styles.periodBtn, {
                  [styles.periodSelected]: period === "AM",
                })}
                onClick={() => setPeriod("AM")}
                type="button"
              >
                AM
              </button>
              <button
                className={classNames(styles.periodBtn, {
                  [styles.periodSelected]: period === "PM",
                })}
                onClick={() => setPeriod("PM")}
                type="button"
              >
                PM
              </button>
            </div>
          </div>

          {/* Actions row */}
          <div className={styles.actionsRow}>
            <AccessTimeOutlined className={styles.clockIcon} />
            <div className={styles.buttons}>
              {actions?.cancel && (
                <Button variant="text" className={styles.btnSky} onClick={handleClose}>
                  <b>{locale.cancel}</b>
                </Button>
              )}
              {actions?.ok && (
                <Button variant="text" className={styles.btnSky} onClick={handleOk}>
                  <b>{locale.ok}</b>
                </Button>
              )}
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default TimePickerInput;
